import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Button, Input, Checkbox, Modal, Dropdown, Spin } from 'antd';
import { useAntdAppMessage } from '../../hooks/useAntdAppMessage';
import type { MenuProps } from 'antd';
import {
  DownloadOutlined,
  SendOutlined,
  EditOutlined,
  DeleteOutlined,
  PushpinFilled,
  MoreOutlined,
  UserOutlined,
  SearchOutlined
} from '@ant-design/icons';
import projectCardStyles from '../ProjectCard/ProjectCard.module.scss';
import { useAuth } from '../../hooks/useAuth';
import { useAtom } from 'jotai';
import { userAtom } from '../../store/atoms';
import { useSecureService } from '../../store/service';
import { Shape, DrawingTool } from '../VideoPlayer/VideoPlayer';
import VersionManager from '../VersionManager/VersionManager';
import GuestCommentModal from '../../modals/guestCommentModal';
import DeleteModal from '../DeleteModal/DeleteModal';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import type { RevisdomAPIViewModelsEntryActivityLogResponse } from '../../service/models/RevisdomAPIViewModelsEntryActivityLogResponse';
import type { RevisdomAPIViewModelsEntryCreateCommentRequest } from '../../service/models/RevisdomAPIViewModelsEntryCreateCommentRequest';
import styles from '../../pages/projects/Projects.module.scss';
import { flattenCommentsWithNestedReplies } from '../../utils/flattenVersionComments';
import { Role } from '../../constants/roles';
import {
  getUserDisplayNameWithDeleted,
  getMentionTokenForInput,
  isUserDeleted,
  DELETED_USER_LABEL,
} from '../../utils/userPresence';
import { DeletedUserAvatarShell } from '../UserPresence/DeletedUserAvatarShell';
import deletedUserUi from '../UserPresence/deletedUserUi.module.scss';

dayjs.extend(relativeTime);

const COMMENT_CARD_MENU_ICON_SIZE = 18;
const MAX_PINNED_COMMENTS_PER_FILE = 3;

/** Bugünle aynı takvim günündeyse sadece saat, değilse tarih + saat (örn. 07:55 AM). */
function formatMarkedResolvedAt(updatedAt: string): string {
  const d = dayjs(updatedAt);
  const timeStr = d.format('hh:mm A');
  if (d.isSame(dayjs(), 'day')) {
    return `Marked as resolved at ${timeStr}`;
  }
  return `Marked as resolved at ${d.format('DD.MM.YYYY')}, ${timeStr}`;
}

function commentDataHasShapes(data: string | undefined | null): boolean {
  if (data == null || data === '') return false;
  try {
    const parsed = JSON.parse(data) as { shapes?: unknown };
    return Array.isArray(parsed.shapes) && parsed.shapes.length > 0;
  } catch {
    return false;
  }
}

/** Aktivite açıklaması nokta ile bitiyorsa (cümle sonu) gösterimden düşür */
function trimActivityDescriptionEnd(desc: string | null | undefined): string {
  const t = (desc ?? '').trim();
  if (!t) return '';
  return t.endsWith('.') ? t.slice(0, -1).trimEnd() : t;
}

/** When old or new is API string "true"/"false" (pin, resolve, etc.), hide the old→new row */
function isActivityBooleanString(v: string | null | undefined): boolean {
  if (v == null) return false;
  const t = String(v).trim().toLowerCase();
  return t === 'true' || t === 'false';
}

/** Reply zincirinde kök yorum id’si: üst yorum yoksa kendi id; varsa köke kadar çıkılır (reply’nin reply’si yok). */
export function getThreadRootCommentIdForReply(
  comment: CommentType,
  byId: Map<number, CommentType>,
): number {
  let cur: CommentType | undefined = comment;
  const visited = new Set<number>();
  while (cur != null && cur.parentCommentId != null) {
    if (visited.has(cur.id)) break;
    visited.add(cur.id);
    const pid = cur.parentCommentId;
    const p = byId.get(pid);
    if (!p) {
      return pid;
    }
    cur = p;
  }
  return cur.id;
}

/** Aynı versiyon görünümündeki düz listede kök yorum nesnesi (üst zinciri `byId` üzerinden). */
function getThreadRootComment(
  comment: CommentType,
  byId: Map<number, CommentType>,
): CommentType {
  let cur: CommentType | undefined = comment;
  const visited = new Set<number>();
  while (cur != null && cur.parentCommentId != null) {
    if (visited.has(cur.id)) break;
    visited.add(cur.id);
    const p = byId.get(cur.parentCommentId);
    if (!p) break;
    cur = p;
  }
  return cur;
}

/** PDF: if the root comment's page range includes the current page, list the whole thread */
function filterCommentsByPdfPage(comments: CommentType[], page: number): CommentType[] {
  if (comments.length === 0) return comments;
  const byId = new Map(comments.map((c) => [c.id, c]));
  const overlapsPage = (c: CommentType) => {
    const lo = Math.min(c.startFrame, c.endFrame);
    const hi = Math.max(c.startFrame, c.endFrame);
    return page >= lo && page <= hi;
  };
  const rootIdsOnPage = new Set<number>();
  for (const c of comments) {
    if (c.parentCommentId != null) continue;
    if (overlapsPage(c)) rootIdsOnPage.add(c.id);
  }
  return comments.filter((c) => {
    const root = getThreadRootComment(c, byId);
    return rootIdsOnPage.has(root.id);
  });
}

// Comment type definition
export type CommentType = {
  id: number;
  userId: number;
  versionId: number;
  /** Üst yorum (reply ise dolu); kök yorumda null/undefined */
  parentCommentId?: number | null;
  startFrame: number;
  endFrame: number;
  text: string;
  data: string;
  createdAt: string;
  updatedAt?: string | null;
  isResolved?: boolean;
  isPinned?: boolean | null;
  lastPinnedDate?: string | null;
  shapes?: Shape[];
  versionName?: string;
  user?: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    title: string;
    role: string;
    organizationName?: string | null;
    isActive: boolean;
    createdAt: string;
    lastLoginAt?: string | null;
    thumbnailUrl?: string;
  };
};

interface CommentSectionProps {
  fileDetail: any;
  versions: any[];
  versionsLoading: boolean;
  selectedVersion: number;
  selectedVersionCompare?: number | null;
  selectedCommentId?: number | null;
  showAllComments: boolean;
  onShowAllCommentsChange: (show: boolean) => void;
  onVersionChange: (version: string) => void;
  canComment: boolean;
  canShowAllComments: boolean;
  canResolve: boolean;
  userRole?: string;
  /** Yeni versiyon / POST /api/File — Admin, SuperAdmin, Editor (canCreate ile aynı) */
  canAddFileVersion: boolean;
  onAddNewFile: () => void;
  
  // Video player states
  compareMode: boolean;
  activePlayer: number;
  currentFrameRange: string;
  currentFrameRangeCompare: string;
  
  // Drawing states
  shapes: Shape[];
  shapesCompare: Shape[];
  drawingTool: DrawingTool;
  drawingColor: string;
  selectedShapeId: string | null;
  selectedShapeIdCompare: string | null;
  onShapesChange: (shapes: Shape[]) => void;
  onShapesCompareChange: (shapes: Shape[]) => void;
  onToolChange: (tool: DrawingTool) => void;
  onColorChange: (color: string) => void;
  onClearShapes: () => void;
  onSave: () => void;
  onDeleteSelected: () => void;
  onDeleteSelectedCompare: () => void;
  onChangeSelectedColor: (color: string) => void;
  onChangeSelectedColorCompare: (color: string) => void;
  onUndo: () => void;
  onRedo: () => void;
  onUndoCompare: () => void;
  onRedoCompare: () => void;
  canUndo: boolean;
  canRedo: boolean;
  canUndoCompare: boolean;
  canRedoCompare: boolean;
  
  // Video time states
  currentVideoTime: number;
  currentFps: number;
  videoDuration: number;

  /** PDF: page number is sent as startFrame/endFrame (page 1 = frame 1) */
  viewerMediaKind?: 'video' | 'image' | 'pdf' | 'other';
  pdfCurrentPage?: number;
  /** Current page at submit time (avoids state lag) */
  pdfPageRef?: React.MutableRefObject<number>;
  pdfNumPages?: number | null;
  /** PDF: show all pages’ comments (like Show All Comments in compare) */
  pdfShowAllPageComments?: boolean;
  onPdfShowAllPageCommentsChange?: (show: boolean) => void;
  
  // Frame selection states
  selectedFrame: number | null;
  selectedFrameRange: [number, number] | null;
  selectedFrameCompare: number | null;
  selectedFrameRangeCompare: [number, number] | null;
  
  // Comment handlers
  onCommentSelect: (comment: CommentType) => void;
  onCommentSelectCompare: (comment: CommentType) => void;
  /** Yorum kartı dışına tıklanınca seçimi kaldır */
  onCommentSelectionClear?: () => void;
  onGuestCommentSubmit: (commentData: any, guestData: any) => void;
  onFileDetailRefresh: () => void;
  
  // Close drawing popover
  closeDrawingPopover: boolean;
  onCloseDrawingPopover: (close: boolean) => void;
  
  // Reset frame selection
  onResetFrameSelection: () => void;
  /** Yorum gönderildikten sonra araç, seçim ve undo geçmişi */
  onDrawingToolbarReset?: () => void;
  /** Versiyon seçici üst çubukta gösterildiğinde yorum sütunundakini gizle */
  hideVersionControls?: boolean;
  /** ⋯ menü görünümü: ProjectCard ile aynı (internal / external tema) */
  cardMenuVariant?: 'internal' | 'external';
  /**
   * Reply modu: parent yorum çizimi gizlenir, boş tuval; null = reply bitti (seçili yorum varsa çizim geri yüklenir).
   */
  onReplyCompositionChange?: (targetPlayer: 1 | 2 | null) => void;
}

const CommentSection: React.FC<CommentSectionProps> = ({
  fileDetail,
  versions,
  versionsLoading,
  selectedVersion,
  selectedVersionCompare,
  selectedCommentId,
  showAllComments,
  onShowAllCommentsChange,
  onVersionChange,
  canComment,
  canShowAllComments,
  canResolve,
  userRole,
  canAddFileVersion,
  onAddNewFile,
  compareMode,
  activePlayer,
  currentFrameRange,
  currentFrameRangeCompare,
  shapes,
  shapesCompare,
  drawingTool,
  drawingColor,
  selectedShapeId,
  selectedShapeIdCompare,
  onShapesChange,
  onShapesCompareChange,
  onToolChange,
  onColorChange,
  onClearShapes,
  onSave,
  onDeleteSelected,
  onDeleteSelectedCompare,
  onChangeSelectedColor,
  onChangeSelectedColorCompare,
  onUndo,
  onRedo,
  onUndoCompare,
  onRedoCompare,
  canUndo,
  canRedo,
  canUndoCompare,
  canRedoCompare,
  currentVideoTime,
  currentFps,
  videoDuration,
  viewerMediaKind = 'other',
  pdfCurrentPage = 1,
  pdfPageRef,
  pdfNumPages = null,
  pdfShowAllPageComments = true,
  onPdfShowAllPageCommentsChange,
  selectedFrame,
  selectedFrameRange,
  selectedFrameCompare,
  selectedFrameRangeCompare,
  onCommentSelect,
  onCommentSelectCompare,
  onCommentSelectionClear,
  onGuestCommentSubmit,
  onFileDetailRefresh,
  closeDrawingPopover,
  onCloseDrawingPopover,
  onResetFrameSelection,
  onDrawingToolbarReset,
  hideVersionControls = false,
  cardMenuVariant = 'internal',
  onReplyCompositionChange
}) => {
  const message = useAntdAppMessage();
  const onCommentSelectionClearRef = useRef(onCommentSelectionClear);
  onCommentSelectionClearRef.current = onCommentSelectionClear;

  const { user, canViewFileActivityLogs, canPinComment } = useAuth();
  const [, setUser] = useAtom(userAtom);
  const {
    createFileComment,
    createGuestFileComment,
    resolveFileComment,
    pinFileComment,
    editFileComment,
    deleteFileComment,
    getFileById,
    getFileActivityLogs,
  } = useSecureService();

  const getFileActivityLogsRef = useRef(getFileActivityLogs);
  getFileActivityLogsRef.current = getFileActivityLogs;
  
  // Comment input state
  const [commentListInput, setCommentListInput] = useState('');
  
  type CommentPanelTab = 'comments' | 'resolved' | 'activity';
  const [commentPanelTab, setCommentPanelTab] = useState<CommentPanelTab>('comments');

  const [activityLogs, setActivityLogs] = useState<RevisdomAPIViewModelsEntryActivityLogResponse[]>(
    [],
  );
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState<string | null>(null);

  const commentPanelTabs = useMemo(() => {
    const tabs: { id: CommentPanelTab; label: string }[] = [
      { id: 'comments', label: 'Comments' },
      { id: 'resolved', label: 'Resolved' },
    ];
    if (canViewFileActivityLogs) {
      tabs.push({ id: 'activity', label: 'Activity' });
    }
    return tabs;
  }, [canViewFileActivityLogs]);

  useEffect(() => {
    if (commentPanelTab === 'activity' && !canViewFileActivityLogs) {
      setCommentPanelTab('comments');
    }
  }, [commentPanelTab, canViewFileActivityLogs]);

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent('showResolvedChanged', { detail: commentPanelTab === 'resolved' })
    );
  }, [commentPanelTab]);

  useEffect(() => {
    if (commentPanelTab !== 'activity' || !canViewFileActivityLogs || !fileDetail?.id) {
      return;
    }
    let cancelled = false;
    setActivityLoading(true);
    setActivityError(null);
    void getFileActivityLogsRef.current(fileDetail.id).then((res: any) => {
      if (cancelled) return;
      setActivityLoading(false);
      if (res?.success && res?.data?.items && Array.isArray(res.data.items)) {
        setActivityLogs(res.data.items as RevisdomAPIViewModelsEntryActivityLogResponse[]);
      } else {
        setActivityLogs([]);
        if (!res?.success) {
          setActivityError(
            typeof res?.message === 'string' ? res.message : 'Activity could not be loaded.',
          );
        }
      }
    });
    return () => {
      cancelled = true;
    };
  }, [commentPanelTab, canViewFileActivityLogs, fileDetail?.id]);

  useEffect(() => {
    if (!closeDrawingPopover) return;
    onToolChange('none');
    onCloseDrawingPopover(false);
  }, [closeDrawingPopover, onCloseDrawingPopover, onToolChange]);
  
  // Mention states
  const [isMentionDropdownVisibleList, setIsMentionDropdownVisibleList] = useState(false);
  const [mentionSearchTermList, setMentionSearchTermList] = useState('');
  const [isMentionDisabledList, setIsMentionDisabledList] = useState(false);
  const [mentionMapping, setMentionMapping] = useState<{[key: string]: string}>({});
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  
  // Guest comment modal states
  const [isGuestCommentModalOpen, setIsGuestCommentModalOpen] = useState(false);
  const [pendingCommentData, setPendingCommentData] = useState<any>(null);
  const [guestCommentLoading, setGuestCommentLoading] = useState(false);
  
  // Resolve loading states
  const [resolvingComments, setResolvingComments] = useState<{[key: number]: boolean}>({});

  const [pinningComments, setPinningComments] = useState<Record<number, boolean>>({});

  const [commentFooterMenuOpenId, setCommentFooterMenuOpenId] = useState<number | null>(null);

  /** Reply modu: hangi karta Reply denildi (seçimle senkron; değişince iptal) */
  const [replyContextCommentId, setReplyContextCommentId] = useState<number | null>(null);
  /** API’ye giden parentCommentId (her zaman kök thread id) */
  const [replyParentCommentId, setReplyParentCommentId] = useState<number | null>(null);

  const prevReplyParentCommentIdRef = useRef<number | null>(null);
  useEffect(() => {
    const prev = prevReplyParentCommentIdRef.current;
    prevReplyParentCommentIdRef.current = replyParentCommentId;
    if (prev != null && replyParentCommentId == null) {
      onReplyCompositionChange?.(null);
    }
  }, [replyParentCommentId, onReplyCompositionChange]);

  const FPS = 30;
  // Edit comment states
  const [, setEditingCommentId] = useState<number | null>(null);
  const [editCommentText, setEditCommentText] = useState('');
  const [editCommentLoading, setEditCommentLoading] = useState(false);
  
  // Delete comment states
  const [, setDeletingCommentId] = useState<number | null>(null);
  const [deleteCommentLoading, setDeleteCommentLoading] = useState(false);

  // Edit modal states
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editTargetCommentId, setEditTargetCommentId] = useState<number | null>(null);

  // Delete modal states
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteTargetCommentId, setDeleteTargetCommentId] = useState<number | null>(null);

  // Check if current user can edit/delete comment
  const canEditDeleteComment = (comment: CommentType) => {
    if (!user || user.role === 'Guest') return false;
    return user.email === comment.user?.email;
  };

  // Handle edit comment
  const handleEditComment = (comment: CommentType) => {
    setEditingCommentId(comment.id);
    setEditCommentText(comment.text || '');
    setEditTargetCommentId(comment.id);
    setIsEditModalOpen(true);
  };

  // Handle save edit
  const handleSaveEdit = async () => {
    if (!editTargetCommentId || !editCommentText.trim()) return;
    
    setEditCommentLoading(true);
    try {
      const response = await editFileComment(editTargetCommentId, {
        text: editCommentText.trim(),
        data: ''
      });
      
      if (response.success) {
        setIsEditModalOpen(false);
        setEditingCommentId(null);
        setEditTargetCommentId(null);
        setEditCommentText('');
        onFileDetailRefresh();
        message.success('Comment updated successfully');
      } else {
        message.error(response.message || 'Failed to update comment');
      }
    } catch (error) {
      message.error('An error occurred while updating the comment');
    } finally {
      setEditCommentLoading(false);
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setIsEditModalOpen(false);
    setEditingCommentId(null);
    setEditTargetCommentId(null);
    setEditCommentText('');
  };

  // Handle delete comment
  const handleDeleteComment = async (commentId: number) => {
    setIsDeleteModalOpen(true);
    setDeleteTargetCommentId(commentId);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTargetCommentId) return;
    setDeleteCommentLoading(true);
    try {
      const response = await deleteFileComment(deleteTargetCommentId);
      if (response.success) {
        onFileDetailRefresh();
        message.success('Comment deleted successfully');
        setIsDeleteModalOpen(false);
        setDeleteTargetCommentId(null);
      } else {
        message.error(response.message || 'Failed to delete comment');
      }
    } catch (error) {
      message.error('An error occurred while deleting the comment');
    } finally {
      setDeleteCommentLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setDeleteTargetCommentId(null);
  };

  const countPinnedInFile = () => {
    let n = 0;
    for (const v of fileDetail?.versions ?? []) {
      for (const c of flattenCommentsWithNestedReplies(v.comments)) {
        if (c.isPinned === true) n++;
      }
    }
    return n;
  };

  const handlePinToggle = async (e: React.MouseEvent, item: CommentType) => {
    e.stopPropagation();
    const nextPinned = !item.isPinned;
    if (nextPinned && countPinnedInFile() >= MAX_PINNED_COMMENTS_PER_FILE) {
      message.warning('You can pin up to 3 comments.');
      return;
    }
    setPinningComments((prev) => ({ ...prev, [item.id]: true }));
    try {
      const res = await pinFileComment(item.id, nextPinned);
      if (res?.success) {
        onFileDetailRefresh();
      } else {
        message.error(
          typeof res?.message === 'string' ? res.message : 'Could not update pin.',
        );
      }
    } catch {
      message.error('Something went wrong while updating pin.');
    } finally {
      setPinningComments((prev) => {
        const next = { ...prev };
        delete next[item.id];
        return next;
      });
    }
  };

  const handleResolveToggle = async (e: React.MouseEvent, item: CommentType) => {
    e.stopPropagation();
    const newValue = !item.isResolved;
    setResolvingComments((prev) => ({ ...prev, [item.id]: true }));
    try {
      const res = await resolveFileComment(item.id, newValue);
      if (res && res.success && fileDetail?.id) {
        setTimeout(async () => {
          const refreshRes = await getFileById(fileDetail.id);
          if (refreshRes.success && refreshRes.data) {
            onFileDetailRefresh();
          }
          setResolvingComments((prev) => ({ ...prev, [item.id]: false }));
        }, 800);
      } else {
        setResolvingComments((prev) => ({ ...prev, [item.id]: false }));
      }
    } catch {
      setResolvingComments((prev) => ({ ...prev, [item.id]: false }));
    }
  };

  // Scroll to selected comment when selectedCommentId changes
  useEffect(() => {
    if (selectedCommentId) {
      // DOM'un render edilmesini bekle
      setTimeout(() => {
        const commentElement = document.getElementById(`comment-${activePlayer}-${selectedCommentId}`);
        if (commentElement) {
          commentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }, [selectedCommentId, activePlayer]);

  useEffect(() => {
    if (selectedCommentId == null) return;
    const onMouseDown = (e: MouseEvent) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (
        t.closest('[data-comment-card]') ||
        t.closest('[data-activity-log-card]') ||
        t.closest('[data-comment-compose]')
      ) {
        return;
      }
      if (
        t.closest(
          '.ant-dropdown,.ant-dropdown-menu,.rc-dropdown,.ant-select-dropdown,.ant-modal-wrap,.ant-modal-root,.ant-tooltip,.ant-popover,.ant-picker-dropdown,.ant-image-preview-root,.ant-message,.ant-notification'
        )
      ) {
        return;
      }
      onCommentSelectionClearRef.current?.();
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [selectedCommentId]);

  useEffect(() => {
    if (selectedCommentId == null) {
      setReplyContextCommentId(null);
      setReplyParentCommentId(null);
      return;
    }
    if (replyContextCommentId != null && selectedCommentId !== replyContextCommentId) {
      setReplyContextCommentId(null);
      setReplyParentCommentId(null);
    }
  }, [selectedCommentId, replyContextCommentId]);

  // Get accessible versions for guest users
  const getAccessibleVersions = () => {
    if (!user || user.role !== 'Guest') {
      return fileDetail?.versions || [];
    }
    return fileDetail?.versions || [];
  };

  const accessibleVersions = getAccessibleVersions();

  /** Listeyle aynı kural: kök thread resolve ise Resolved sekmesi */
  const resolveCommentPanelTabForComment = (comment: CommentType): 'comments' | 'resolved' => {
    const v = accessibleVersions.find((x: any) => x.id === comment.versionId);
    if (!v) return 'comments';
    const flat = flattenCommentsWithNestedReplies(v.comments).map(
      (c: any) =>
        ({
          ...c,
          versionId: v.id,
          versionName: v.name || `v${v.id}`,
        }) as CommentType,
    );
    const byId = new Map(flat.map((c) => [c.id, c]));
    const root = getThreadRootComment(comment, byId);
    return root.isResolved ? 'resolved' : 'comments';
  };

  const commentByIdLookup = useMemo(() => {
    const map = new Map<number, CommentType>();
    const vers = fileDetail?.versions || [];
    for (const v of vers) {
      for (const c of flattenCommentsWithNestedReplies(v.comments)) {
        map.set(Number(c.id), {
          ...c,
          versionId: v.id,
          versionName: v.name || `v${v.id}`,
        });
      }
    }
    return map;
  }, [fileDetail?.versions]);

  const handleReplyClick = useCallback(
    (e: React.MouseEvent, item: CommentType) => {
      e.stopPropagation();
      const full = commentByIdLookup.get(item.id) ?? item;
      const rootId = getThreadRootCommentIdForReply(full, commentByIdLookup);
      setReplyContextCommentId(item.id);
      setReplyParentCommentId(rootId);

      let replyTargetPlayer: 1 | 2 | null = null;
      if (compareMode) {
        const isVersionInCompare = [selectedVersion, selectedVersionCompare]
          .filter(Boolean)
          .includes(item.versionId);
        if (!isVersionInCompare) {
          message.warning(
            'This version is not available in compare mode. Turn off compare to open this version.',
          );
          return;
        }
        if (item.versionId === selectedVersion) {
          onCommentSelect(item);
          replyTargetPlayer = 1;
        } else if (item.versionId === selectedVersionCompare) {
          onCommentSelectCompare(item);
          replyTargetPlayer = 2;
        }
      } else if (activePlayer === 1) {
        onCommentSelect(item);
        replyTargetPlayer = 1;
      } else {
        onCommentSelectCompare(item);
        replyTargetPlayer = 2;
      }
      if (replyTargetPlayer != null) {
        onReplyCompositionChange?.(replyTargetPlayer);
      }
    },
    [
      activePlayer,
      commentByIdLookup,
      compareMode,
      onCommentSelect,
      onCommentSelectCompare,
      onReplyCompositionChange,
      selectedVersion,
      selectedVersionCompare,
    ],
  );

  const resolveVersionKeyForChange = (versionId: number): string | null => {
    const v = versions.find((x: any) => x.id === versionId);
    if (!v) return null;
    if (v.name) return v.name;
    if (v.path) {
      const parts = String(v.path).split('/');
      const last = parts[parts.length - 1];
      if (last) return last;
    }
    return String(versionId);
  };

  const findCommentFromActivityLog = (
    log: RevisdomAPIViewModelsEntryActivityLogResponse,
  ): CommentType | null => {
    const cid = log.commentId;
    if (cid == null) return null;
    for (const v of accessibleVersions) {
      const flat = flattenCommentsWithNestedReplies(v.comments);
      const c = flat.find((x: any) => Number(x.id) === Number(cid));
      if (c) {
        return {
          ...c,
          versionId: v.id,
          versionName: v.name || `v${v.id}`,
        };
      }
    }
    return null;
  };

  const applyVersionHash = (versionId: number) => {
    const vMeta = versions.find((x: any) => x.id === versionId);
    if (vMeta?.name) window.location.hash = vMeta.name;
    else if (vMeta?.path) {
      const pathParts = String(vMeta.path).split('/');
      const vn = pathParts[pathParts.length - 1];
      if (vn) window.location.hash = vn;
    }
  };

  const handleActivityLogCardClick = (log: RevisdomAPIViewModelsEntryActivityLogResponse) => {
    const item = findCommentFromActivityLog(log);
    const effectiveVersionId = log.versionId ?? item?.versionId ?? null;

    if (effectiveVersionId == null) {
      if (log.commentId != null) {
        message.warning('Comment or version not found.');
      }
      return;
    }

    const versionKey = resolveVersionKeyForChange(effectiveVersionId);
    if (!versionKey) {
      message.warning('Version not found.');
      return;
    }

    if (compareMode) {
      if (item) {
        const isVersionInCompare = [selectedVersion, selectedVersionCompare]
          .filter(Boolean)
          .includes(item.versionId);
        if (!isVersionInCompare) {
          message.warning(
            'This version is not available in compare mode. Turn off compare to open this version.',
          );
          return;
        }
        setCommentPanelTab(resolveCommentPanelTabForComment(item));
        if (item.versionId === selectedVersion) {
          onShapesCompareChange([]);
          onCommentSelect(item);
        } else if (item.versionId === selectedVersionCompare) {
          onShapesChange([]);
          onCommentSelectCompare(item);
        }
        return;
      }
      const inCompare = [selectedVersion, selectedVersionCompare]
        .filter(Boolean)
        .includes(effectiveVersionId);
      if (!inCompare) {
        message.warning(
          'This version is not available in compare mode. Turn off compare to open this version.',
        );
        return;
      }
    }

    onShowAllCommentsChange(false);

    if (item) {
      setCommentPanelTab(resolveCommentPanelTabForComment(item));
      onCommentSelect(item);
      applyVersionHash(item.versionId);
      return;
    }

    setCommentPanelTab('comments');
    onVersionChange(versionKey);
    applyVersionHash(effectiveVersionId);
    onCommentSelectionClear?.();
  };

  const getAccessibleComments = (comments: CommentType[]) => comments;

  const filterCommentsByPanelTab = (comments: CommentType[]): CommentType[] => {
    if (commentPanelTab === 'activity') return [];
    const byId = new Map(comments.map((c) => [c.id, c]));
    const threadRootResolved = (c: CommentType) =>
      !!getThreadRootComment(c, byId).isResolved;
    if (commentPanelTab === 'comments') {
      return comments.filter((c) => !threadRootResolved(c));
    }
    if (commentPanelTab === 'resolved') {
      return comments.filter((c) => threadRootResolved(c));
    }
    return [];
  };

  // Handle mention click for comment list input
  const handleMentionClickList = () => {
    setIsMentionDropdownVisibleList(!isMentionDropdownVisibleList);
    setMentionSearchTermList('');
    setSelectedMentionIndex(0);
  };

  // Handle mention select for comment list input
  const handleCommentListMentionSelect = (user: any) => {
    const displayText = getMentionTokenForInput(user);

    const lastAtIndex = commentListInput.lastIndexOf('@');
    let newComment: string;
    if (lastAtIndex !== -1) {
      // @ ile açılan mention: @ ve sonrasındaki sorguyu seçilen token ile değiştir
      const beforeAt = commentListInput.substring(0, lastAtIndex);
      newComment = beforeAt + displayText + ' ';
    } else {
      // Alttaki butonla açıldıysa @ olmayabilir — mevcut yorumu silme, sadece ekle
      const prefix = commentListInput;
      const sep = prefix.length > 0 && !/\s$/.test(prefix) ? ' ' : '';
      newComment = prefix + sep + displayText + ' ';
    }

    setCommentListInput(newComment);
    
    // Add to mention mapping
    setMentionMapping(prev => ({
      ...prev,
      [displayText]: user.email
    }));
    
    setIsMentionDropdownVisibleList(false);
    setMentionSearchTermList('');
    setIsMentionDisabledList(false);
  };

  // Handle comment input change
  const handleCommentListChange = (value: string) => {
    setCommentListInput(value);

    const currentLastAtIndex = value.lastIndexOf('@');
    const previousLastAtIndex = commentListInput.lastIndexOf('@');

    // Re-enable mention if new @ is typed while disabled
    if (isMentionDisabledList && currentLastAtIndex > previousLastAtIndex) {
      setIsMentionDisabledList(false);
      const afterAt = value.substring(currentLastAtIndex + 1);
      setMentionSearchTermList(afterAt);
      setIsMentionDropdownVisibleList(true);
      setSelectedMentionIndex(0);
      return;
    }

    // Skip if mention is disabled
    if (isMentionDisabledList) {
      return;
    }

    // Check for @ symbol
    if (currentLastAtIndex !== -1) {
      const afterAt = value.substring(currentLastAtIndex + 1);
      if (!afterAt.includes(' ')) {
        setMentionSearchTermList(afterAt);
        setIsMentionDropdownVisibleList(true);
        setSelectedMentionIndex(0);
      } else {
        setIsMentionDropdownVisibleList(false);
      }
    } else {
      setIsMentionDropdownVisibleList(false);
    }
  };

  // Get filtered mention users for comment list input
  const filteredMentionUsersList = fileDetail?.share?.filter((user: any) => {
    if (!mentionSearchTermList) return true;
    const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
    const email = user.email.toLowerCase();
    const search = mentionSearchTermList.toLowerCase();
    return fullName.includes(search) || email.includes(search);
  }) || [];

  // Handle comment send
  const handleCommentSend = async () => {
    if (!fileDetail?.id) {
      alert('File information not found. Please refresh the page.');
      return;
    }
    
    // Check if there's text or drawing
    if (!commentListInput.trim() && shapes.length === 0) {
      return;
    }

    const isPdf = viewerMediaKind === 'pdf';
    const effFps = currentFps > 0 ? currentFps : FPS;

    let startFrame = 1;
    let endFrame = isPdf ? 1 : Math.round(videoDuration * FPS);

    if (isPdf) {
      /* Video timeline fields may still be set in PDF mode; frame = currently open page */
      const livePage =
        pdfPageRef != null && pdfPageRef.current >= 1
          ? pdfPageRef.current
          : pdfCurrentPage > 0
            ? pdfCurrentPage
            : 1;
      if (
        selectedFrameRange != null &&
        selectedFrameRange[1] > selectedFrameRange[0]
      ) {
        startFrame = selectedFrameRange[0];
        endFrame = selectedFrameRange[1];
      } else {
        startFrame = livePage;
        endFrame = livePage;
      }
      const maxP =
        typeof pdfNumPages === 'number' && pdfNumPages > 0 ? pdfNumPages : 999999;
      startFrame = Math.max(1, Math.min(maxP, startFrame));
      endFrame = Math.max(1, Math.min(maxP, endFrame));
    } else {
      if (currentFrameRange) {
        const frameParts = currentFrameRange.split('-');
        if (frameParts.length === 2) {
          startFrame = parseInt(frameParts[0]);
          endFrame = parseInt(frameParts[1]);
        } else {
          startFrame = parseInt(frameParts[0]);
          endFrame = startFrame;
        }
      } else if (selectedFrame !== null) {
        startFrame = selectedFrame;
        endFrame = selectedFrame;
      } else if (selectedFrameRange !== null) {
        startFrame = selectedFrameRange[0];
        endFrame = selectedFrameRange[1];
      } else {
        const currentFrame = Math.round(currentVideoTime * FPS);
        startFrame = currentFrame;
        endFrame = currentFrame;
      }
    }

    // Validate and correct frame values
    if (startFrame > endFrame) {
      [startFrame, endFrame] = [endFrame, startFrame];
    }

    if (endFrame < startFrame) endFrame = startFrame;

    if (startFrame > endFrame) endFrame = startFrame;

    const data = isPdf
      ? {
          videoTime: Math.max(0, (startFrame - 1) / effFps),
          fps: effFps,
          shapes: shapes || [],
          pdfPage: startFrame,
        }
      : {
          videoTime: currentVideoTime,
          fps: currentFps,
          shapes: shapes || [],
        };

    // Process mentions
    let processedText = commentListInput.trim() || '-';
    
    Object.keys(mentionMapping).forEach(mentionText => {
      const email = mentionMapping[mentionText];
      processedText = processedText.replace(mentionText, `{{${email}}}`);
    });

    // Determine target version ID based on active player in compare mode
    let targetVersionId = selectedVersion;
    if (compareMode && activePlayer === 2 && selectedVersionCompare) {
      targetVersionId = selectedVersionCompare;
    }

    const commentData: RevisdomAPIViewModelsEntryCreateCommentRequest = {
      versionId: targetVersionId,
      startFrame,
      endFrame,
      text: processedText,
      data: JSON.stringify(data),
    };
    if (replyParentCommentId != null) {
      commentData.parentCommentId = replyParentCommentId;
    }

    // Open guest modal for non-logged users
    if (!user) {
      setPendingCommentData(commentData);
      setIsGuestCommentModalOpen(true);
      return;
    }

    try {
      const response = await createFileComment(fileDetail.id, commentData);
      if (response.success) {
        // Reset all states after successful comment
        setCommentListInput('');
        setMentionMapping({});
        setIsMentionDropdownVisibleList(false);
        setMentionSearchTermList('');
        setIsMentionDisabledList(false);
        setReplyContextCommentId(null);
        setReplyParentCommentId(null);

        // Reset drawing states
        onShapesChange([]);
        onShapesCompareChange([]);
        onDrawingToolbarReset?.();
        onCloseDrawingPopover(true);

        // Reset frame selection
        onResetFrameSelection();

        // Refresh file detail to get updated comments
        onFileDetailRefresh();
      } else {
        alert(`Comment could not be sent: ${response.message || 'Unknown error'}`);
      }
    } catch (error: any) {
      alert('An error occurred while sending the comment.');
    }
  };

  // Handle guest comment submit
  const handleGuestCommentSubmit = async (guestData: { firstName: string; lastName: string; email: string }) => {
    if (!pendingCommentData || !fileDetail?.id) {
      alert('Comment data not found.');
      return;
    }

    setGuestCommentLoading(true);
    try {
      let processedText = pendingCommentData.text || '-';
      
      Object.keys(mentionMapping).forEach(mentionText => {
        const email = mentionMapping[mentionText];
        processedText = processedText.replace(mentionText, `{{${email}}}`);
      });

      const response = await createGuestFileComment(fileDetail.id, {
        ...pendingCommentData,
        text: processedText,
        ...guestData
      });

      if (response.success) {
        // Reset all states after successful guest comment
        setCommentListInput('');
        setMentionMapping({});
        setIsMentionDropdownVisibleList(false);
        setMentionSearchTermList('');
        setIsMentionDisabledList(false);
        setReplyContextCommentId(null);
        setReplyParentCommentId(null);

        // Reset drawing states
        onShapesChange([]);
        onShapesCompareChange([]);
        onDrawingToolbarReset?.();
        setIsGuestCommentModalOpen(false);
        setPendingCommentData(null);
        
        // Close drawing toolboxes and popovers
        onCloseDrawingPopover(true);
        
        // Reset frame selection
        onResetFrameSelection();
        
        // Guest kullanıcıyı session'a ekle
        setUser({
          id: 0,
          firstName: guestData.firstName,
          lastName: guestData.lastName,
          email: guestData.email,
          role: 'Guest',
          title: 'Guest User',
          organizationName: null,
          isActive: true,
          createdAt: new Date().toISOString(),
          lastLoginAt: new Date().toISOString()
        });
        
        // Refresh file detail to get updated comments
        onFileDetailRefresh();
      } else {
        alert(`Comment could not be sent: ${response.message || 'Unknown error'}`);
      }
    } catch (error: any) {
      alert('An error occurred while sending the comment.');
    } finally {
      setGuestCommentLoading(false);
    }
  };

  // Export comments to Excel
  const handleExportComments = () => {
    if (commentPanelTab === 'activity') {
      message.warning('Comments export is not available in Activity tab');
      return;
    }
    if (!versions?.length) {
      message.error('No comments to export');
      return;
    }

    let allComments: CommentType[] = [];
    
    if (showAllComments) {
      versions.forEach((version: any) => {
        const versionComments = flattenCommentsWithNestedReplies(version.comments).map(
          (comment: any) => ({
            ...comment,
            versionId: version.id,
            versionName: version.name,
          }),
        );
        allComments = [...allComments, ...versionComments];
      });
    } else {
      const selectedVersionData = versions.find((v: any) => v.id === selectedVersion);
      if (selectedVersionData) {
        allComments = flattenCommentsWithNestedReplies(selectedVersionData.comments).map(
          (comment: any) => ({
            ...comment,
            versionId: selectedVersionData.id,
            versionName: selectedVersionData.name,
          }),
        );
      }
    }

    allComments = getAccessibleComments(allComments);
    allComments = filterCommentsByPanelTab(allComments);

    if (
      fileType === 'pdf' &&
      !compareMode &&
      !pdfShowAllPageComments &&
      pdfCurrentPage != null &&
      pdfCurrentPage >= 1
    ) {
      allComments = filterCommentsByPdfPage(allComments, pdfCurrentPage);
    }

    if (allComments.length === 0) {
      message.error('No comments to export');
      return;
    }

    // Prepare data for Excel
    const excelData = allComments.map((comment: CommentType) => ({
      'Date': new Date(comment.createdAt).toLocaleDateString('tr-TR'),
      'Time': new Date(comment.createdAt).toLocaleTimeString('tr-TR'),
      'User': getUserDisplayNameWithDeleted(comment.user, comment.userId ?? null),
      'Role': comment.user?.role || 'Guest',
      'Email': comment.user?.email || '',
      'Version': comment.versionName || `v${comment.versionId}`,
      ...(fileType !== 'image' && {
        'Frame Range': comment.startFrame === comment.endFrame 
          ? `${comment.endFrame}` 
          : `${comment.startFrame}-${comment.endFrame}`
      }),
      'Comment': comment.text || '(No comment)',
      'Resolved': comment.isResolved ? 'Yes' : 'No',
      'Created At': new Date(comment.createdAt).toLocaleString('tr-TR'),
      'Updated At': comment.updatedAt ? new Date(comment.updatedAt).toLocaleString('tr-TR') : ''
    }));

    // Convert to XLSX format
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Comments');
    
    // Download file
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    const safeEntryName = String(fileDetail.name)
      .replace(/[/\\?%*:|"<>]/g, '_')
      .trim()
      .replace(/\s+/g, '_');
    const dateToken = dayjs().format('DDMMYY');
    const exportSuffix = commentPanelTab === 'resolved' ? 'Resolved' : 'Comments';
    link.setAttribute('download', `Revisdom_${safeEntryName}_${exportSuffix}_${dateToken}.xlsx`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    message.success('Comments exported successfully!');
  };

  const handleExportActivityLog = useCallback(() => {
    if (!fileDetail?.name) {
      message.error('File is not loaded');
      return;
    }
    if (activityLoading) {
      return;
    }
    if (activityError) {
      message.error('Activity log could not be loaded; nothing to export');
      return;
    }
    if (!activityLogs.length) {
      message.warning('No activity to export');
      return;
    }

    const excelData = activityLogs.map((log) => ({
      Date: log.createdAt ? dayjs(log.createdAt).format('DD.MM.YYYY') : '',
      Time: log.createdAt ? dayjs(log.createdAt).format('hh:mm A') : '',
      Description: log.description ?? '',
      User: getUserDisplayNameWithDeleted(log.user, null),
      Email: log.user?.email ?? '',
      Role: log.user?.role ?? '',
      'Old value': log.oldValue ?? '',
      'New value': log.newValue ?? '',
      Version: log.versionName ?? '',
      'Version ID': log.versionId ?? '',
      'Comment ID': log.commentId ?? '',
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Activity');

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    const safeEntryName = String(fileDetail.name)
      .replace(/[/\\?%*:|"<>]/g, '_')
      .trim()
      .replace(/\s+/g, '_');
    const dateToken = dayjs().format('DDMMYY');
    link.setAttribute(
      'download',
      `Revisdom_${safeEntryName}_ActivityLogs_${dateToken}.xlsx`,
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    message.success('Activity log exported successfully');
  }, [activityError, activityLoading, activityLogs, fileDetail?.name]);

  const getFileType = (fileName: string, videoUrl?: string) => {
    let extension: string | undefined = undefined;
    if (fileName && fileName.includes('.')) {
      const parts = fileName.split('.');
      if (parts.length > 1) {
        extension = parts.pop()?.toLowerCase();
      }
    }
    if ((!extension || extension === fileName.toLowerCase()) && videoUrl) {
      const cleanUrl = videoUrl.split('?')[0].split('#')[0];
      const urlExtension = cleanUrl.split('.').pop()?.toLowerCase().trim();
      if (urlExtension && urlExtension.length <= 5) {
        const cleanExtension = urlExtension.replace(/[^a-z0-9]/gi, '');
        if (cleanExtension) {
          extension = cleanExtension;
        }
      }
    }
    if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'].includes(extension || '')) {
      return 'video';
    }
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'ico'].includes(extension || '')) {
      return 'image';
    }
    if (['pdf'].includes(extension || '')) {
      return 'pdf';
    }
    if (['doc', 'docx'].includes(extension || '')) {
      return 'document';
    }
    return 'other';
  };

  const fileUrlForType =
    fileDetail?.versions?.find((v: any) => v.id === selectedVersion)?.fileUrl ||
    fileDetail?.versions?.[0]?.fileUrl ||
    fileDetail?.url ||
    fileDetail?.fileUrl ||
    fileDetail?.downloadUrl ||
    fileDetail?.path ||
    '';
  const selectedVersionDataForType = fileDetail?.versions?.find(
    (v: any) => v.id === selectedVersion,
  );
  const fileNameForType =
    selectedVersionDataForType?.name ||
    (selectedVersionDataForType?.path
      ? String(selectedVersionDataForType.path).split('/').pop()
      : undefined) ||
    fileDetail?.name ||
    '';
  const fileType = getFileType(fileNameForType, fileUrlForType);

  return (
    <>
      <div className={styles.commentSectionColumn}>
      {!hideVersionControls && !compareMode && (
        <div className={styles.versionSelectContainer}>
          <VersionManager
            fileDetail={fileDetail}
            versions={versions}
            versionsLoading={versionsLoading}
            selectedVersion={selectedVersion}
            compareMode={compareMode}
            onVersionChange={onVersionChange}
          />
          {canAddFileVersion && (
            <Button type="primary" onClick={onAddNewFile}>
              + Add Version
            </Button>
          )}
        </div>
      )}
      
      <div className={styles.commentListContainer}>
        <div className={styles.commentPanelTabs} role="tablist" aria-label="Comment panels">
          {commentPanelTabs.map((tab) => {
            const activeTabFlushTopRight =
              tab.id === 'activity' || (tab.id === 'resolved' && !canViewFileActivityLogs);
            return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={commentPanelTab === tab.id}
              className={
                commentPanelTab === tab.id
                  ? [
                      styles.commentPanelTab,
                      styles.commentPanelTabActive,
                      activeTabFlushTopRight && styles.commentPanelTabActiveActivity
                    ]
                      .filter(Boolean)
                      .join(' ')
                  : styles.commentPanelTab
              }
              onClick={() => setCommentPanelTab(tab.id)}
            >
              {tab.label}
            </button>
            );
          })}
        </div>
        {versions.length > 1 &&
          canShowAllComments &&
          compareMode &&
          commentPanelTab !== 'activity' && (
            <div className={styles.showAllCommentsContainerInline}>
              <Checkbox
                checked={showAllComments}
                onChange={(e) => onShowAllCommentsChange(e.target.checked)}
                className={styles.showAllCommentsCheckbox}
              >
                Show All Comments
              </Checkbox>
            </div>
          )}
        {fileType === 'pdf' &&
          !compareMode &&
          onPdfShowAllPageCommentsChange &&
          commentPanelTab !== 'activity' && (
            <div className={styles.showAllCommentsContainerInline}>
              <Checkbox
                checked={pdfShowAllPageComments}
                onChange={(e) => onPdfShowAllPageCommentsChange(e.target.checked)}
                className={styles.showAllCommentsCheckbox}
              >
                Show comments from all pages
              </Checkbox>
            </div>
          )}
        <div className={styles.commentListScrollShell}>
        <div className={styles.commentListContent}>
          {/* Render grouped comments */}
          {(() => {
            if (commentPanelTab === 'activity') {
              if (activityLoading) {
                return (
                  <div className={styles.activityLogLoading}>
                    <Spin size="small" />
                  </div>
                );
              }
              if (activityError) {
                return <div className={styles.commentTabEmpty}>{activityError}</div>;
              }
              if (!activityLogs.length) {
                return (
                  <div className={styles.commentTabEmpty}>No activity yet.</div>
                );
              }
              return (
                <div className={styles.activityLogAsComments}>
                  {activityLogs.map((log) => {
                    const displayName = getUserDisplayNameWithDeleted(log.user, null);
                    const deletedUser = isUserDeleted(log.user);
                    const isGuestUser =
                      !deletedUser &&
                      String(log.user?.role ?? '')
                        .toLowerCase()
                        .includes('guest');
                    const navigable = log.versionId != null || log.commentId != null;
                    const headerDesc = trimActivityDescriptionEnd(log.description) || '—';
                    return (
                      <div
                        key={log.id ?? `${String(log.createdAt)}-${log.description ?? ''}`}
                        className={styles.commentItem}
                      >
                        <div
                          role={navigable ? 'button' : undefined}
                          tabIndex={navigable ? 0 : undefined}
                          data-activity-log-card
                          className={styles.commentCard}
                          style={{
                            position: 'relative',
                            cursor: navigable ? 'pointer' : 'default',
                          }}
                          onClick={() => navigable && handleActivityLogCardClick(log)}
                          onKeyDown={(e) => {
                            if (!navigable) return;
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleActivityLogCardClick(log);
                            }
                          }}
                        >
                          <div className={styles.commentCardHeader}>
                            <div className={styles.commentCardHeaderLeft}>
                              <div
                                className={styles.commentAvatar}
                                style={{
                                  backgroundColor: log.user?.thumbnailUrl
                                    ? 'transparent'
                                    : isGuestUser
                                      ? '#FFC107'
                                      : undefined,
                                  backgroundImage: log.user?.thumbnailUrl ? 'none' : undefined,
                                }}
                              >
                                <DeletedUserAvatarShell deleted={deletedUser}>
                                  {log.user?.thumbnailUrl ? (
                                    <img
                                      src={log.user.thumbnailUrl}
                                      alt={displayName}
                                      style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        borderRadius: '50%',
                                      }}
                                    />
                                  ) : (
                                    (() => {
                                      const fn = log.user?.firstName || '';
                                      const ln = log.user?.lastName || '';
                                      const a = fn.charAt(0).toUpperCase();
                                      const b = ln.charAt(0).toUpperCase();
                                      return (a + b) || (log.user?.email?.charAt(0).toUpperCase() ?? '?');
                                    })()
                                  )}
                                </DeletedUserAvatarShell>
                              </div>
                              <div className={styles.commentCardHeaderMeta}>
                                <span className={styles.activityLogHeaderByline}>
                                  <span className={styles.activityLogDescInHeader}>{headerDesc}</span>
                                  <span className={styles.activityLogByConnector}> by </span>
                                  <span
                                    className={`${styles.activityLogNameInHeader} ${deletedUser ? deletedUserUi.nameDeleted : ''}`}
                                    style={isGuestUser ? { color: '#FFC107' } : undefined}
                                  >
                                    {displayName}
                                  </span>
                                </span>
                                {log.createdAt ? (
                                  <span
                                    className={styles.commentRelativeTime}
                                    title={dayjs(log.createdAt).format('DD.MM.YYYY HH:mm')}
                                  >
                                    {dayjs(log.createdAt).fromNow()}
                                  </span>
                                ) : null}
                              </div>
                            </div>
                          </div>
                          <div className={`${styles.commentCardBody} ${styles.activityLogBody}`}>
                            {(() => {
                              if (
                                isActivityBooleanString(log.oldValue) ||
                                isActivityBooleanString(log.newValue)
                              ) {
                                return null;
                              }
                              const hasOld =
                                log.oldValue != null && String(log.oldValue).trim() !== '';
                              const hasNew =
                                log.newValue != null && String(log.newValue).trim() !== '';
                              if (!hasOld && !hasNew) return null;
                              return (
                                <div className={styles.activityLogValues}>
                                  {hasOld ? (
                                    <span className={styles.activityLogOld}>{log.oldValue}</span>
                                  ) : null}
                                  {hasOld && hasNew ? (
                                    <span className={styles.activityLogArrow}> → </span>
                                  ) : null}
                                  {hasNew ? (
                                    <span className={styles.activityLogNew}>{log.newValue}</span>
                                  ) : null}
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            }
            // Get comments for active player
            const _selectedVersionDataCompare = accessibleVersions.find((v: any) => v.id === selectedVersionCompare);
            let commentsCompare: CommentType[] = [];
            
            if (showAllComments) {
              // Compare modunda ise sadece compare edilen versiyonların yorumlarını göster
              if (compareMode) {
                // Compare edilen iki versiyonun yorumlarını göster
                const compareVersions = [selectedVersion, selectedVersionCompare].filter(Boolean);
                accessibleVersions.forEach((version: any) => {
                  if (compareVersions.includes(version.id)) {
                    const versionComments = flattenCommentsWithNestedReplies(version.comments).map(
                      (comment: any) => ({
                        ...comment,
                        versionId: version.id,
                        versionName: version.name || `v${version.id}`,
                      }),
                    );
                    commentsCompare = [...commentsCompare, ...versionComments];
                  }
                });
              } else {
                // Normal modda tüm versiyonların yorumlarını göster
                accessibleVersions.forEach((version: any) => {
                  const versionComments = flattenCommentsWithNestedReplies(version.comments).map(
                    (comment: any) => ({
                      ...comment,
                      versionId: version.id,
                      versionName: version.name || `v${version.id}`,
                    }),
                  );
                  commentsCompare = [...commentsCompare, ...versionComments];
                });
              }
            } else {
              // showAllComments false ise aktif player'daki versiyonun yorumlarını göster
              if (compareMode) {
                // Compare modunda aktif player'a göre versiyon seç
                let targetVersion: number;
                if (activePlayer === 1) {
                  targetVersion = selectedVersion;
                } else {
                  targetVersion = selectedVersionCompare || selectedVersion;
                }
                
                const selectedVersionData = accessibleVersions.find((v: any) => v.id === targetVersion);
                if (selectedVersionData) {
                  commentsCompare = flattenCommentsWithNestedReplies(selectedVersionData.comments).map(
                    (comment: any) => ({
                      ...comment,
                      versionId: selectedVersionData.id,
                      versionName: selectedVersionData.name || `v${selectedVersionData.id}`,
                    }),
                  );
                }
              } else {
                // Normal modda seçili versiyonun yorumlarını göster
                const selectedVersionData = accessibleVersions.find((v: any) => v.id === selectedVersion);
                if (selectedVersionData) {
                  commentsCompare = flattenCommentsWithNestedReplies(selectedVersionData.comments).map(
                    (comment: any) => ({
                      ...comment,
                      versionId: selectedVersionData.id,
                      versionName: selectedVersionData.name || `v${selectedVersionData.id}`,
                    }),
                  );
                }
              }
            }

            commentsCompare = filterCommentsByPanelTab(commentsCompare);

            if (
              fileType === 'pdf' &&
              !compareMode &&
              !pdfShowAllPageComments &&
              pdfCurrentPage != null &&
              pdfCurrentPage >= 1
            ) {
              commentsCompare = filterCommentsByPdfPage(commentsCompare, pdfCurrentPage);
            }

            const currentComments = commentsCompare;

            const commentByIdCurrent = new Map(currentComments.map((c) => [c.id, c]));
            const isRootInThreadList = (c: CommentType) =>
              c.parentCommentId == null || !commentByIdCurrent.has(c.parentCommentId);

            const collectThreadReplies = (rootId: number): CommentType[] => {
              const byParent = new Map<number, CommentType[]>();
              for (const c of currentComments) {
                const pid = c.parentCommentId;
                if (pid == null) continue;
                if (!byParent.has(pid)) byParent.set(pid, []);
                byParent.get(pid)!.push(c);
              }
              const out: CommentType[] = [];
              const walk = (pid: number) => {
                const kids = (byParent.get(pid) || [])
                  .slice()
                  .sort(
                    (a, b) =>
                      new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
                  );
                for (const k of kids) {
                  out.push(k);
                  walk(k.id);
                }
              };
              walk(rootId);
              return out;
            };

            const pinnedCommentsForDisplay = currentComments
              .filter((c) => c.isPinned === true && isRootInThreadList(c))
              .sort((a, b) => {
                const ta = a.lastPinnedDate ? new Date(a.lastPinnedDate).getTime() : 0;
                const tb = b.lastPinnedDate ? new Date(b.lastPinnedDate).getTime() : 0;
                if (tb !== ta) return tb - ta;
                return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
              });

            const unpinnedForGrouping = currentComments.filter(
              (c) => c.isPinned !== true && isRootInThreadList(c),
            );

            const groupedCommentsForDisplay = unpinnedForGrouping.reduce(
              (groups: any, comment: CommentType) => {
                const date = new Date(comment.createdAt).toLocaleDateString('tr-TR');
                if (!groups[date]) {
                  groups[date] = [];
                }
                groups[date].push(comment);
                return groups;
              },
              {} as Record<string, CommentType[]>,
            );

            Object.keys(groupedCommentsForDisplay).forEach((date) => {
              groupedCommentsForDisplay[date].sort(
                (a: CommentType, b: CommentType) =>
                  new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
              );
            });

            const dateEntries = Object.entries(groupedCommentsForDisplay);

            const commentIsSelected = (c: CommentType) => {
              if (compareMode) {
                if (c.versionId === selectedVersion && selectedCommentId === c.id) return true;
                if (c.versionId === selectedVersionCompare && selectedCommentId === c.id) return true;
                return false;
              }
              if (activePlayer === 1 && selectedCommentId === c.id) return true;
              if (activePlayer === 2 && selectedCommentId === c.id) return true;
              return false;
            };

            const exitReplyCompositionIfActive = () => {
              if (replyParentCommentId == null) return;
              onReplyCompositionChange?.(null);
              setReplyParentCommentId(null);
              setReplyContextCommentId(null);
            };

            const handleCommentCardActivate = (clicked: CommentType) => {
              if (compareMode) {
                const isVersionInCompare = [selectedVersion, selectedVersionCompare].includes(
                  clicked.versionId,
                );
                if (!isVersionInCompare) {
                  message.warning(
                    'This version is not available in compare mode. Turn off compare to open this version.',
                  );
                  return;
                }
                exitReplyCompositionIfActive();
                if (clicked.versionId === selectedVersion) {
                  onCommentSelect(clicked);
                  onShapesCompareChange([]);
                } else if (clicked.versionId === selectedVersionCompare) {
                  onCommentSelectCompare(clicked);
                  onShapesChange([]);
                }
              } else {
                exitReplyCompositionIfActive();
                if (activePlayer === 1) {
                  onCommentSelect(clicked);
                } else {
                  onCommentSelectCompare(clicked);
                }
              }
            };

            const renderCommentThreadBlock = (item: CommentType, isNested: boolean) => {
              const isCommentCardSelected = commentIsSelected(item);
              const commentAuthorDeleted = isUserDeleted(item.user);
              const isGuestCommentAuthor =
                !commentAuthorDeleted && item.user?.role === 'Guest';
              return (
                <div
                  key={`thread-block-${item.id}`}
                  id={`comment-${activePlayer}-${item.id}`}
                  data-comment-card
                  className={`${styles.commentCardThreadBlock}${
                    isNested ? ` ${styles.commentCardNestedReply}` : ''
                  }${isCommentCardSelected ? ` ${styles.commentCardSelected}` : ''}`}
                  role="presentation"
                  onClick={() => handleCommentCardActivate(item)}
                >
                            <div
                              className={`${styles.commentCardHeader}${
                                item.isResolved ? ` ${styles.commentCardHeaderResolved}` : ''
                              }`}
                            >
                              <div className={styles.commentCardHeaderLeft}>
                                <div
                                  className={styles.commentAvatar}
                                  style={{
                                    backgroundColor: item.user?.thumbnailUrl
                                      ? 'transparent'
                                      : isGuestCommentAuthor
                                        ? '#FFC107'
                                        : undefined,
                                    backgroundImage: item.user?.thumbnailUrl ? 'none' : undefined,
                                  }}
                                >
                                  <DeletedUserAvatarShell deleted={commentAuthorDeleted}>
                                    {item.user?.thumbnailUrl ? (
                                      <img
                                        src={item.user.thumbnailUrl}
                                        alt={`${item.user?.firstName || ''} ${item.user?.lastName || ''}`}
                                        style={{
                                          width: '100%',
                                          height: '100%',
                                          objectFit: 'cover',
                                          borderRadius: '50%',
                                        }}
                                      />
                                    ) : (
                                      (() => {
                                        const firstName = item.user?.firstName || '';
                                        const lastName = item.user?.lastName || '';
                                        const firstLetter = firstName.charAt(0).toUpperCase();
                                        const lastLetter = lastName.charAt(0).toUpperCase();
                                        return firstLetter + lastLetter;
                                      })()
                                    )}
                                  </DeletedUserAvatarShell>
                                </div>
                                <div className={styles.commentCardHeaderMeta}>
                                  <span
                                    className={`${styles.commentUserName} ${commentAuthorDeleted ? deletedUserUi.nameDeleted : ''}`}
                                    style={{
                                      color: isGuestCommentAuthor ? '#FFC107' : undefined,
                                    }}
                                  >
                                    {getUserDisplayNameWithDeleted(item.user, item.userId ?? null)}
                                  </span>
                                  {(!item.isResolved || showAllComments) && (
                                    <div className={styles.commentMetaSecondRow}>
                                      {!item.isResolved && (
                                        <span
                                          className={styles.commentRelativeTime}
                                          title={new Date(item.createdAt).toLocaleString()}
                                        >
                                          {dayjs(item.createdAt).fromNow()}
                                        </span>
                                      )}
                                      {showAllComments && (
                                        <span
                                          className={styles.commentVersionLabel}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            const version = versions.find(v => v.id === item.versionId);
                                            if (version) {
                                              if (compareMode) {
                                                const isVersionInCompare = [selectedVersion, selectedVersionCompare].includes(item.versionId);
                                                if (!isVersionInCompare) {
                                                  message.warning('This version is not available in compare mode. Turn off compare to open this version.');
                                                  return;
                                                }
                                              }
                                              onVersionChange(item.versionId.toString());
                                              onShowAllCommentsChange(false);
                                              if (version.name) {
                                                window.location.hash = version.name;
                                              } else if (version.path) {
                                                const pathParts = version.path.split('/');
                                                const versionName = pathParts[pathParts.length - 1];
                                                if (versionName) {
                                                  window.location.hash = versionName;
                                                }
                                              }
                                            }
                                          }}
                                          title={`Go to ${item.versionName || `Version ${item.versionId}`}`}
                                        >
                                          {item.versionName || `v${item.versionId}`}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                              {!isNested &&
                              (canResolve || (canPinComment && !item.isResolved)) ? (
                              <div className={styles.commentCardHeaderRight}>
                                {canResolve && (
                                  <div
                                    className={styles.commentHeaderResolve}
                                    onClick={(e) => handleResolveToggle(e, item)}
                                    role="presentation"
                                  >
                                    <span
                                      className={`${styles.commentResolveLabel} ${item.isResolved ? styles.resolved : ''} ${resolvingComments[item.id] ? styles.resolving : ''}`}
                                    >
                                      {resolvingComments[item.id] ? 'Resolving...' : (item.isResolved ? 'Resolved' : 'Resolve')}
                                    </span>
                                    <span
                                      className={`${styles.commentResolveCheckbox} ${item.isResolved ? styles.resolved : ''} ${resolvingComments[item.id] ? styles.resolving : ''}`}
                                    >
                                      {item.isResolved && !resolvingComments[item.id] && (
                                        <svg width={8} height={8} viewBox="0 0 12 12">
                                          <polyline
                                            points="2,7 5,10 10,3"
                                            style={{ fill: 'none', stroke: '#F6FFED', strokeWidth: 2 }}
                                          />
                                        </svg>
                                      )}
                                      {resolvingComments[item.id] && (
                                        <div className={styles.resolveLoadingSpinner} />
                                      )}
                                    </span>
                                    <input
                                      type="checkbox"
                                      id={`resolve-${activePlayer}-${item.id}`}
                                      className={styles.commentResolveInputHidden}
                                      checked={!!item.isResolved}
                                      readOnly
                                      tabIndex={-1}
                                      aria-hidden
                                    />
                                  </div>
                                )}
                                {canPinComment && !item.isResolved && (
                                  <button
                                    type="button"
                                    disabled={!!pinningComments[item.id]}
                                    className={
                                      item.isPinned
                                        ? `${styles.commentCardPinBtn} ${styles.commentCardPinBtnPinned}`
                                        : styles.commentCardPinBtn
                                    }
                                    aria-label={item.isPinned ? 'Unpin comment' : 'Pin comment'}
                                    aria-pressed={!!item.isPinned}
                                    title={
                                      pinningComments[item.id]
                                        ? '…'
                                        : item.isPinned
                                          ? 'Pinned'
                                          : 'Pin'
                                    }
                                    onClick={(e) => handlePinToggle(e, item)}
                                  >
                                    {pinningComments[item.id] ? (
                                      <span className={styles.resolveLoadingSpinner} />
                                    ) : (
                                      <PushpinFilled style={{ fontSize: 16 }} />
                                    )}
                                  </button>
                                )}
                              </div>
                              ) : null}
                            </div>

                            <div className={styles.commentCardBody}>
                              <div className={styles.commentMessage}>
                                {(() => {
                                  const hasDrawing = commentDataHasShapes(item.data);
                                  const drawingIcon = hasDrawing ? (
                                    <span
                                      className={styles.commentMessageDrawingIcon}
                                      aria-hidden
                                      title="Drawing"
                                    />
                                  ) : null;

                                  const text = item.text || '';
                                  if (!text) {
                                    return (
                                      <>
                                        <i className={styles.commentNoMessage}>(No comment)</i>
                                        {drawingIcon}
                                      </>
                                    );
                                  }

                                  const emailRegex = /\{\{([^}]+)\}\}/g;
                                  const parts = text.split(emailRegex);
                                  const matches = text.match(emailRegex);

                                  if (!matches) {
                                    return (
                                      <>
                                        {text}
                                        {drawingIcon}
                                      </>
                                    );
                                  }

                                  const result: (string | JSX.Element)[] = [];
                                  let matchIndex = 0;

                                  parts.forEach((part, index) => {
                                    if (index % 2 === 0) {
                                      if (part) result.push(part);
                                    } else {
                                      const email = matches[matchIndex].replace(/\{\{|\}\}/g, '');
                                      const user = fileDetail?.share?.find((u: any) => u.email === email);

                                      if (user) {
                                        const atBase = `@${user.firstName || user.email?.split('@')[0] || 'user'}`;
                                        result.push(
                                          <span
                                            key={`mention-${item.id}-${matchIndex}`}
                                            style={{
                                              color: 'var(--app-accent-color)',
                                              fontWeight: '600',
                                              opacity: isUserDeleted(user) ? 0.72 : undefined,
                                            }}
                                          >
                                            {atBase}
                                            {isUserDeleted(user) ? (
                                              <span className={deletedUserUi.deletedSuffix}>
                                                {' '}
                                                {DELETED_USER_LABEL}
                                              </span>
                                            ) : null}
                                          </span>
                                        );
                                      } else {
                                        result.push(
                                          <span key={`mention-${item.id}-${matchIndex}`} style={{ color: 'var(--app-accent-color)', fontWeight: '600' }}>
                                            {email}
                                          </span>
                                        );
                                      }
                                      matchIndex++;
                                    }
                                  });

                                  return (
                                    <>
                                      {result}
                                      {drawingIcon}
                                    </>
                                  );
                                })()}
                              </div>
                              {!item.isResolved && (
                                <div className={styles.commentCardFooter}>
                                  <button
                                    type="button"
                                    className={[
                                      styles.commentCardReply,
                                      replyContextCommentId === item.id ? styles.commentCardReplyActive : '',
                                    ]
                                      .filter(Boolean)
                                      .join(' ')}
                                    onClick={(e) => handleReplyClick(e, item)}
                                  >
                                    Reply
                                  </button>
                                  {canEditDeleteComment(item) && (
                                    <div
                                      className={[
                                        styles.commentCardFooterHoverActions,
                                        commentFooterMenuOpenId === item.id
                                          ? styles.commentCardFooterHoverActionsVisible
                                          : ''
                                      ]
                                        .filter(Boolean)
                                        .join(' ')}
                                    >
                                      <Dropdown
                                        trigger={['click']}
                                        open={commentFooterMenuOpenId === item.id}
                                        onOpenChange={(open) =>
                                          setCommentFooterMenuOpenId((prev) =>
                                            open ? item.id : prev === item.id ? null : prev
                                          )
                                        }
                                        placement="bottomRight"
                                        getPopupContainer={() => document.body}
                                        destroyPopupOnHide
                                        menu={{
                                          className: `${projectCardStyles.cardMenuOverlay}${
                                            cardMenuVariant === 'external'
                                              ? ` ${projectCardStyles.cardMenuOverlayExternal}`
                                              : ''
                                          }`,
                                          items: [
                                            {
                                              key: 'edit',
                                              label: 'Edit',
                                              icon: (
                                                <EditOutlined
                                                  style={{ fontSize: COMMENT_CARD_MENU_ICON_SIZE }}
                                                />
                                              )
                                            },
                                            {
                                              key: 'delete',
                                              label: 'Delete',
                                              icon: (
                                                <DeleteOutlined
                                                  style={{ fontSize: COMMENT_CARD_MENU_ICON_SIZE }}
                                                />
                                              ),
                                              danger: true
                                            }
                                          ] as MenuProps['items'],
                                          onClick: ({ key, domEvent }) => {
                                            domEvent?.stopPropagation();
                                            setCommentFooterMenuOpenId((prev) =>
                                              prev === item.id ? null : prev
                                            );
                                            if (key === 'edit') handleEditComment(item);
                                            if (key === 'delete') handleDeleteComment(item.id);
                                          }
                                        }}
                                      >
                                        <button
                                          type="button"
                                          className={projectCardStyles.cardMenuTrigger}
                                          aria-label="Menu"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                          }}
                                        >
                                          <MoreOutlined
                                            style={{ fontSize: 16, color: 'rgba(255,255,255,0.7)' }}
                                          />
                                        </button>
                                      </Dropdown>
                                    </div>
                                  )}
                                </div>
                              )}
                              {!isNested && item.isResolved && (
                                <div className={styles.commentResolvedAt}>
                                  {item.updatedAt
                                    ? formatMarkedResolvedAt(item.updatedAt)
                                    : 'Marked as resolved'}
                                </div>
                              )}
                            </div>
                </div>
              );
            };

            const renderCommentCardRow = (rootItem: CommentType, idx: number) => {
              const threadReplies = collectThreadReplies(rootItem.id);
              const shouldDimForReply =
                replyParentCommentId != null && rootItem.id !== replyParentCommentId;
              return (
                        <div
                          key={`${activePlayer}-${rootItem.id || idx}`}
                          id={`comment-thread-${activePlayer}-${rootItem.id}`}
                          className={[
                            styles.commentItem,
                            shouldDimForReply ? styles.commentItemReplyDimmed : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                        >
                          <div className={styles.commentCard} style={{ position: 'relative' }}>
                            {renderCommentThreadBlock(rootItem, false)}
                            {threadReplies.map((r) => renderCommentThreadBlock(r, true))}
                          </div>
                        </div>
              );
            };

            if (pinnedCommentsForDisplay.length === 0 && dateEntries.length === 0) {
              return (
                <div className={styles.commentTabEmpty}>
                  {commentPanelTab === 'resolved' ? 'No resolved comments.' : 'No comments yet.'}
                </div>
              );
            }

            return (
              <>
                {pinnedCommentsForDisplay.length > 0 && (
                  <div key="__pinned-comments">
                    <div className={styles.commentDateGroup}>
                      <div className={styles.commentDateHeader}>Pinned</div>
                      <div className={styles.commentDateUnderline} />
                      {pinnedCommentsForDisplay.map((item: CommentType, idx: number) =>
                        renderCommentCardRow(item, idx),
                      )}
                    </div>
                  </div>
                )}
                {dateEntries.map(([date, dateComments]: [string, any]) => (
                  <div key={date}>
                    <div className={styles.commentDateGroup}>
                      <div className={styles.commentDateHeader}>{date}</div>
                      <div className={styles.commentDateUnderline} />
                      {dateComments.map((item: CommentType, idx: number) =>
                        renderCommentCardRow(item, idx),
                      )}
                    </div>
                  </div>
                ))}
              </>
            );
          })()}
        </div>
        <div className={styles.commentListBottomFade} aria-hidden />
        </div>

        {commentPanelTab === 'activity' && canViewFileActivityLogs && (
          <div className={styles.activityLogFooter}>
            <button
              type="button"
              className={styles.activityLogExportBtn}
              onClick={handleExportActivityLog}
              disabled={activityLoading || !!activityError || activityLogs.length === 0}
              aria-label="Export activity log"
            >
              <img
                src="/images/icon/export.svg"
                alt=""
                width={13}
                height={13}
                className={styles.activityLogExportBtnIcon}
                aria-hidden
              />
              Export Activity Log
            </button>
          </div>
        )}

        {/* Comment input area */}
        {commentPanelTab === 'resolved' && (
          <div className={styles.activityLogFooter}>
            <button
              type="button"
              className={styles.activityLogExportBtn}
              onClick={handleExportComments}
              aria-label="Export resolved comments"
            >
              <img
                src="/images/icon/export.svg"
                alt=""
                width={13}
                height={13}
                className={styles.activityLogExportBtnIcon}
                aria-hidden
              />
              Export Resolved Comments
            </button>
          </div>
        )}

        {commentPanelTab === 'comments' && userRole === Role.Viewer && (
          <div className={styles.activityLogFooter}>
            <button
              type="button"
              className={styles.activityLogExportBtn}
              onClick={handleExportComments}
              aria-label="Export comments"
            >
              <img
                src="/images/icon/export.svg"
                alt=""
                width={13}
                height={13}
                className={styles.activityLogExportBtnIcon}
                aria-hidden
              />
              Export Comments
            </button>
          </div>
        )}

        {commentPanelTab === 'comments' && userRole !== Role.Viewer && (
        <div className={styles.commentListInputContainer} data-comment-compose>
          {/* Input area */}
          <div className={styles.commentInputArea}>
            <Input.TextArea
              className={styles.commentInputNoBorder}
              placeholder={
                replyParentCommentId != null
                  ? 'Leave your reply here...'
                  : 'Write your comment...'
              }
              value={commentListInput}
              onChange={e => handleCommentListChange(e.target.value)}
              autoSize={{ minRows: 2, maxRows: 4 }}
              onKeyDown={(e) => {
                if (isMentionDropdownVisibleList && filteredMentionUsersList.length > 0) {
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setSelectedMentionIndex((prev) => (prev + 1) % filteredMentionUsersList.length);
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setSelectedMentionIndex((prev) => (prev - 1 + filteredMentionUsersList.length) % filteredMentionUsersList.length);
                  } else if (e.key === 'Escape') {
                    setIsMentionDropdownVisibleList(false);
                  }
                }
              }}
              onPressEnter={e => { 
                if (!e.shiftKey) { 
                  e.preventDefault(); 
                  if (isMentionDropdownVisibleList && filteredMentionUsersList.length > 0) {
                    const user = filteredMentionUsersList[selectedMentionIndex];
                    if (user) {
                      handleCommentListMentionSelect(user);
                    }
                  } else {
                  handleCommentSend(); 
                  }
                } 
              }}
            />
          </div>
          
          {/* Button area */}
          <div className={styles.commentButtonArea}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Button
                icon={<DownloadOutlined />}
                className={styles.commentExportBtn}
                onClick={() => handleExportComments()}
                title="Export to Excel"
              />
              <div style={{ position: 'relative' }}>
                <Button
                  icon={<UserOutlined />}
                  className={styles.commentMentionBtnNoBorder}
                  onClick={handleMentionClickList}
                  title="Mention user"
                  aria-label="Mention user"
                />
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Button
                type="primary"
                shape="circle"
                icon={<SendOutlined />}
                onClick={handleCommentSend}
                className={styles.commentSendBtnNoBorder}
                disabled={!commentListInput.trim() && shapes.length === 0}
              />
            </div>
          </div>
          
          {/* Mention dropdown */}
          {isMentionDropdownVisibleList && (
            <div className={styles.mentionDropdown}>
              <div className={styles.mentionDropdownHeader}>
                <Input
                  placeholder="Search users..."
                  value={mentionSearchTermList}
                  onChange={(e) => setMentionSearchTermList(e.target.value)}
                  allowClear
                  variant="borderless"
                  prefix={<SearchOutlined className={styles.mentionDropdownSearchPrefixIcon} aria-hidden />}
                  className={styles.mentionDropdownSearch}
                />
              </div>
              <div className={styles.mentionDropdownList}>
                {filteredMentionUsersList.length > 0 ? (
                  filteredMentionUsersList.map((user: any, idx: number) => {
                    const muDeleted = isUserDeleted(user);
                    const muGuest = !muDeleted && user.role === 'Guest';
                    return (
                    <div
                      key={user.id}
                      onClick={() => handleCommentListMentionSelect(user)}
                      className={styles.mentionDropdownItem}
                      onMouseEnter={() => setSelectedMentionIndex(idx)}
                    >
                      <div className={styles.mentionDropdownAvatar}>
                        <DeletedUserAvatarShell deleted={muDeleted}>
                          {user.firstName?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase()}
                        </DeletedUserAvatarShell>
                      </div>
                      <div>
                        <div className={`${styles.mentionDropdownUserName} ${muDeleted ? deletedUserUi.nameDeleted : ''}`}>
                          {getUserDisplayNameWithDeleted(user, null)}
                          {muGuest && <span className={styles.guestLabel}> (guest)</span>}
                        </div>
                        <div className={styles.mentionDropdownUserEmail}>
                          {user.email}
                        </div>
                      </div>
                    </div>
                  );
                  })
                ) : (
                  <div className={styles.mentionDropdownEmpty}>
                    No users found
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        )}
      </div>
      </div>
      
      {/* Guest Comment Modal */}
      <GuestCommentModal
        isOpen={isGuestCommentModalOpen}
        onClose={() => setIsGuestCommentModalOpen(false)}
        onSubmit={handleGuestCommentSubmit}
        loading={guestCommentLoading}
      />

      {/* Edit Modal */}
      <Modal
        centered
        title="Edit Comment"
        open={isEditModalOpen}
        onOk={handleSaveEdit}
        onCancel={handleCancelEdit}
        footer={[
          <Button key="cancel" onClick={handleCancelEdit}>
            Cancel
          </Button>,
          <Button key="save" type="primary" loading={editCommentLoading} onClick={handleSaveEdit} style={{ background: 'var(--app-accent-color)', borderColor: 'var(--app-accent-color)' }}>
            Save
          </Button>,
        ]}
      >
        <Input.TextArea
          value={editCommentText}
          onChange={(e) => setEditCommentText(e.target.value)}
          autoSize={{ minRows: 1, maxRows: 3 }}
          onPressEnter={(e) => {
            if (!e.shiftKey) {
              e.preventDefault();
              handleSaveEdit();
            }
          }}
        />
      </Modal>

      <DeleteModal
        visible={isDeleteModalOpen}
        titleOverride="Delete Comment"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        loading={deleteCommentLoading}
        variant={cardMenuVariant === 'external' ? 'external' : 'internal'}
      />
    </>
  );
};

export default CommentSection; 